/**
 * MyDataSection — the Tallennetut tiedot in-page view (033 US1, T024): the
 * consolidated GDPR-access surface (FR-001..FR-003).
 *
 * Renders every category of personal data the application holds about the
 * signed-in member — profile, submitted feedback, and the
 * audit-trail summary — plus the legacy-owned categories as labelled LINKS to
 * their authoritative surface (Kehätieto messaging → /messages, routed on
 * read, never mirrored — Constitution P7). Every category card carries an
 * ownership line (FR-003): managed in this application (correctable /
 * erasable here in later slices) vs managed in another service (read-only,
 * lifecycle owned elsewhere).
 *
 * The proto has no My-data/GDPR surface (checked at pin 78cc6c78), so specs
 * own the content and the 026 hub pattern owns the presentation: a fourth hub
 * entry point swaps the in-page view to this section, which follows the
 * MembershipCertificateSection chrome (subordinate <h3>, rounded-2xl bordered
 * cards, dl field stacks).
 *
 * Consumes the myData port via Suspense (useMyData caches the read). Mount
 * inside PortErrorBoundary(myData) + Suspense — the Profile route owns both.
 *
 * No raw colour literals (FR-013) — token classes only. Mobile-first, 375 px
 * safe (FR-012).
 *
 * Owned by feature 033-data-ownership-and-lifecycle-foundations.
 * Task: T024
 */

import { type JSX, useState } from 'react';
import { Link } from 'react-router';
import type { MessageKeys } from '../../i18n/messages/index.js';
import { useLanguage } from '../../i18n/useLanguage.js';
import { useTranslate } from '../../i18n/useTranslate.js';
import { AnalyticsDataSection } from '../../my-data/AnalyticsDataSection.js';
import { DeleteAccountConfirm } from '../../my-data/DeleteAccountConfirm.js';
import { ExportDataSection } from '../../my-data/ExportDataSection.js';
import { SuccessfulMemberMessagesDataSection } from '../../my-data/SuccessfulMemberMessagesDataSection.js';
import { useMyData, useMyDataAnalyticsReader } from '../../my-data/useMyData.js';
import { Button } from '../../primitives/Button.js';
import { useSession } from '../../session/useSession.js';
import { ChevronRightIcon } from '../../shell/icons.js';
import { MyDataCategoryCard, MyDataEmptyNote, MyDataField } from './MyDataCategoryCard.js';
import { ProfileSectionHeader } from './ProfileSectionHeader.js';

/** Locale-aware calendar-date rendering (the certificate section's RD-007 posture). */
function formatIsoDate(dateString: string, locale: string): string {
  const datePart = dateString.split('T')[0] ?? dateString;
  const [y, m, d] = datePart.split('-').map(Number) as [number, number, number];
  if (Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return dateString;
  }
  const date = new Date(y, m - 1, d);
  const formatter = new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
  return formatter.format(date);
}

// Legacy-link row labels are LOCALIZED HERE, not taken from the API: the API
// has no locale context, so its `label` is canonical-but-Finnish (the spec'd
// wire value). Known categories map to i18n — 'messaging' reuses the /messages
// surface's own title key so the row and the page it links to always carry
// the same name in either locale. An unknown (future) category falls back to
// the API label rather than a wrong or empty one (P7).
const LEGACY_LINK_LABEL_KEYS: Record<string, MessageKeys> = {
  messaging: 'messages.title',
};

const LANGUAGE_NAME_KEYS = {
  fi: 'myData.languageSetting.language.fi',
  sv: 'myData.languageSetting.language.sv',
  en: 'myData.languageSetting.language.en',
} as const satisfies Record<'fi' | 'sv' | 'en', MessageKeys>;

export function MyDataSection(): JSX.Element {
  const t = useTranslate();
  const { active: locale } = useLanguage();
  const view = useMyData();
  const analyticsReader = useMyDataAnalyticsReader();
  const session = useSession();
  const [deleteOpen, setDeleteOpen] = useState(false);

  // After the erasure commits server-side the member's sessions are already
  // hard-deleted (the cascade's step 1). Best-effort clear the now-dead cookie,
  // then a DETERMINISTIC full-page nav to the login start — `window.location.
  // assign` sidesteps the 023 React nav race, and the erased session resolves
  // anonymous on the fresh load.
  // A failed revoke (the session row is already gone) must not strand the
  // member on a dead session (P7), so it is swallowed.
  async function handleErased(): Promise<void> {
    try {
      await session.signOut();
    } catch {
      // The session no longer exists; proceed to the login start regardless.
    }
    window.location.assign('/login');
  }

  const legacyLinkLabel = (link: { category: string; label: string }): string => {
    const key = LEGACY_LINK_LABEL_KEYS[link.category];
    return key === undefined ? link.label : t(key);
  };

  return (
    <div className="flex flex-col gap-4">
      <ProfileSectionHeader title={t('profile.hub.myData')} />
      <p className="text-sm text-muted-foreground">{t('myData.intro')}</p>

      <MyDataCategoryCard
        heading={t('myData.profile.heading')}
        ownership={t('myData.ownership.app')}
      >
        <dl className="flex flex-col">
          <MyDataField label={t('myData.profile.name')} value={view.profile.displayName} />
          <MyDataField label={t('myData.profile.email')} value={view.profile.email} />
          {view.profile.jasennumero !== null && (
            <MyDataField
              label={t('myData.profile.memberNumber')}
              value={view.profile.jasennumero}
            />
          )}
        </dl>
      </MyDataCategoryCard>

      <MyDataCategoryCard
        heading={t('myData.feedback.heading')}
        ownership={t('myData.ownership.app')}
      >
        {view.feedback.length === 0 ? (
          <MyDataEmptyNote>{t('myData.feedback.empty')}</MyDataEmptyNote>
        ) : (
          <ul className="flex flex-col">
            {view.feedback.map((item) => (
              <li
                key={`${item.submittedAt}-${item.rating ?? 'x'}-${item.text ?? ''}`}
                className="border-b border-border px-4 py-3 last:border-b-0"
              >
                <div className="text-sm font-medium text-muted-foreground">
                  {formatIsoDate(item.submittedAt, locale)}
                  {item.rating !== null && ` · ${t('myData.feedback.rating')} ${item.rating}/5`}
                </div>
                {item.text !== null && <p className="mt-1 text-sm text-foreground">{item.text}</p>}
              </li>
            ))}
          </ul>
        )}
      </MyDataCategoryCard>

      <MyDataCategoryCard
        heading={t('myData.contactVerification.heading')}
        ownership={t('myData.ownership.app')}
      >
        {view.contactVerification.lastVerifiedAt === null ? (
          <MyDataEmptyNote>{t('myData.contactVerification.never')}</MyDataEmptyNote>
        ) : (
          <dl className="flex flex-col">
            <MyDataField
              label={t('myData.contactVerification.lastVerified')}
              value={formatIsoDate(view.contactVerification.lastVerifiedAt, locale)}
            />
          </dl>
        )}
      </MyDataCategoryCard>

      <MyDataCategoryCard
        heading={t('myData.languageSetting.heading')}
        ownership={t('myData.ownership.app')}
      >
        {view.languageSetting.language === null ? (
          <MyDataEmptyNote>{t('myData.languageSetting.never')}</MyDataEmptyNote>
        ) : (
          <dl className="flex flex-col">
            <MyDataField
              label={t('myData.languageSetting.chosen')}
              value={t(LANGUAGE_NAME_KEYS[view.languageSetting.language])}
            />
          </dl>
        )}
      </MyDataCategoryCard>

      <MyDataCategoryCard heading={t('myData.audit.heading')} ownership={t('myData.ownership.app')}>
        <dl className="flex flex-col">
          <MyDataField
            label={t('myData.audit.count')}
            value={String(view.auditSummary.sensitiveEventCount)}
          />
          {view.auditSummary.earliest !== null && (
            <MyDataField
              label={t('myData.audit.earliest')}
              value={formatIsoDate(view.auditSummary.earliest, locale)}
            />
          )}
        </dl>
      </MyDataCategoryCard>

      <AnalyticsDataSection analytics={view.analytics} reader={analyticsReader} />

      <SuccessfulMemberMessagesDataSection
        successfulMemberMessages={
          view.analytics.status === 'available'
            ? { status: 'available', ...view.analytics.successfulMemberMessages }
            : { status: 'unavailable' }
        }
        reader={analyticsReader}
      />

      <MyDataCategoryCard
        heading={t('myData.legacy.heading')}
        ownership={t('myData.ownership.legacy')}
      >
        <ul className="flex flex-col">
          {view.legacyLinks.map((link) => (
            <li key={link.category} className="border-b border-border last:border-b-0">
              <Link
                to={link.href}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-foreground transition-colors hover:bg-primary/10"
              >
                <span className="text-sm font-medium">{legacyLinkLabel(link)}</span>
                <ChevronRightIcon
                  className="h-5 w-5 shrink-0 text-muted-foreground"
                  aria-hidden="true"
                />
              </Link>
              {link.category === 'messaging' && (
                <p className="px-4 pb-3 text-xs text-muted-foreground">
                  {t('myData.legacy.messaging.note')}
                </p>
              )}
            </li>
          ))}
        </ul>
      </MyDataCategoryCard>

      {/* Portability entry point (US5, FR-014..FR-016). Its own component: the
          flow carries five phases and this file sits under a 400-line cap. Like
          the erasure block below it is an action, not a data category, so it
          carries no ownership line. */}
      <ExportDataSection />

      {/* Erasure entry point (US2, FR-006). A distinct action block — NOT a
          data-category card — so it carries no app/legacy ownership line. The
          irreversibility confirmation lives in the dialog, mounted only while
          open so a member who never opens it needs no accountDeletion port. */}
      <section
        aria-label={t('myData.delete.section.heading')}
        className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-4"
      >
        <h4 className="text-sm font-semibold text-foreground">
          {t('myData.delete.section.heading')}
        </h4>
        <p className="text-sm text-muted-foreground">{t('myData.delete.section.description')}</p>
        <div>
          <Button type="button" variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
            {t('myData.delete.trigger')}
          </Button>
        </div>
      </section>

      {deleteOpen && (
        <DeleteAccountConfirm
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onCompleted={() => {
            void handleErased();
          }}
        />
      )}
    </div>
  );
}
